import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { useIsPro } from "@/hooks/useIsPro";
import { supabase } from "@/integrations/supabase/client";
import {
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
} from "@/integrations/external-supabase/client";
import { CHECKOUT_STARTED_KEY } from "@/lib/entitlementFlip";
import { isFoundingOfferOpen } from "@/lib/foundingWindow";

type PlanKey = "monthly" | "annual" | "founding";

const CREATE_CHECKOUT_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/create-checkout-session`;

const isMedian = () =>
  typeof navigator !== "undefined" && /median/i.test(navigator.userAgent);

// Same open-in-external-browser fallback chain as useGoogleCalendar.tsx's
// connect() — Median needs the bridge call, plain web just needs a new tab.
function openExternal(url: string) {
  if (isMedian()) {
    try {
      const median = (window as any).median;
      if (median?.window?.open) {
        median.window.open(url, "external");
        return;
      }
    } catch { /* fall through */ }
    try {
      window.location.href = `median://window/open?url=${encodeURIComponent(url)}&mode=external`;
      return;
    } catch { /* fall through */ }
    window.location.href = url;
    return;
  }
  window.open(url, "_blank");
}

function mondayOfThisWeek(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}

const Pro = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session } = useAuth();
  const isAdmin = useAdmin();
  const { adminPreview } = useAuth();
  const isProNow = useIsPro() || (isAdmin && adminPreview);
  const [loadingPlan, setLoadingPlan] = useState<PlanKey | null>(null);
  const [weeklyMinutes, setWeeklyMinutes] = useState<number | null>(null);

  const checkoutStatus = searchParams.get("checkout"); // "success" | "canceled" | null

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("activity_log" as never)
        .select("time_credit, created_at")
        .eq("user_id", user.id)
        .gte("created_at", mondayOfThisWeek().toISOString());
      if (cancelled || error) return;
      const rows = (data ?? []) as unknown as { time_credit: number }[];
      if (rows.length >= 3) {
        setWeeklyMinutes(rows.reduce((sum, r) => sum + (r.time_credit || 0), 0));
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const timeFmt = useMemo(() => {
    if (!weeklyMinutes) return null;
    const h = Math.floor(weeklyMinutes / 60);
    const m = weeklyMinutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }, [weeklyMinutes]);

  const handleUpgrade = async (plan: PlanKey) => {
    if (!session?.access_token) {
      navigate("/signin");
      return;
    }
    setLoadingPlan(plan);
    try {
      localStorage.setItem(CHECKOUT_STARTED_KEY, String(Date.now()));
    } catch { /* ignore */ }
    try {
      const resp = await fetch(CREATE_CHECKOUT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ plan }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error((errData as any).error || `Error ${resp.status}`);
      }
      const { url } = await resp.json();
      if (!url) throw new Error("No checkout URL returned");
      openExternal(url);
    } catch (e) {
      console.error("[Pro] checkout error:", e);
      toast.error("Couldn't start checkout. Try again?");
      try {
        localStorage.removeItem(CHECKOUT_STARTED_KEY);
      } catch { /* ignore */ }
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col" style={{ backgroundColor: "#F0F0FF" }}>
      {/* Header */}
      <header className="px-4 py-4 flex-shrink-0" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
        <div className="flex items-center max-w-2xl mx-auto w-full">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/50 transition-colors"
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M13 4L7 10L13 16" stroke="#1C1A3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="font-display text-center flex-1 mr-10" style={{ fontSize: "22px", color: "#1C1A3E" }}>
            Kin Pro
          </h1>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-14 max-w-2xl mx-auto w-full">
        {checkoutStatus === "canceled" && (
          <div
            className="font-body mb-6"
            style={{
              backgroundColor: "#FFFFFF",
              border: "1.5px solid #C8C8F4",
              color: "#54525C",
              fontSize: 14,
              padding: "14px 16px",
              borderRadius: 14,
              textAlign: "center",
            }}
          >
            No charge made — checkout was canceled.
          </div>
        )}

        {isProNow || checkoutStatus === "success" ? (
          <div className="text-center mt-8">
            <h2 className="font-display" style={{ fontSize: 26, color: "#1C1A3E" }}>
              {checkoutStatus === "success" ? "Welcome to Pro 💜" : "You're already Pro 💜"}
            </h2>
            {/* Unconditional on purpose. This was gated on !isProSetupDone()
                for about an hour and the gate was wrong twice over: the flag
                is per-device localStorage, so a mom who finished setup on her
                phone is told nothing on a laptop and vice versa — and more
                importantly, the framing was remedial. Written as orientation
                instead, it is worth reading whether or not she has been
                through the wizard, so there is nothing left to gate on.

                Note this also renders in Safari, where Stripe's success_url
                actually lands (the Median app opens checkout externally), and
                that is the right place to be told to go back to the app.

                "Settings → Kin Pro" names the destination, not a button: that
                row reads "Finish setting up Kin Pro" or "Replay Pro setup"
                depending on state, so naming either one would be wrong half
                the time.

                Steps are left-aligned inside the centred block on purpose:
                centred numbered lists are genuinely hard to follow. */}
            <p className="font-body mt-3" style={{ fontSize: 15, color: "#54525C" }}>
              Everything Kin can do is unlocked. Two things worth knowing:
            </p>
            <ol
              className="font-body mt-3 mx-auto text-left"
              style={{ fontSize: 15, color: "#54525C", lineHeight: 1.5, maxWidth: 420 }}
            >
              <li style={{ marginBottom: 10 }}>
                <span style={{ fontWeight: 600, color: "#1C1A3E" }}>1.</span> If anything still
                looks locked, close the app all the way (swipe it up and away) and open it fresh —
                that wakes everything up.
              </li>
              <li>
                <span style={{ fontWeight: 600, color: "#1C1A3E" }}>2.</span> Your setup lives in
                Settings → Kin Pro — finish it if you haven't, or replay it anytime, and Kin starts
                remembering your world.
              </li>
            </ol>
            <p className="font-body mt-4" style={{ fontSize: 15, color: "#54525C" }}>
              Head back and let her handle it.
            </p>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div className="text-center mt-4">
              <h2 className="font-display" style={{ fontSize: 30, lineHeight: 1.2, color: "#1C1A3E" }}>
                Let me handle even more for you 💜
              </h2>
              <p className="font-body mt-3" style={{ fontSize: 15, color: "#54525C" }}>
                Pro gives Kin the memory and initiative to carry more of the mental load, automatically.
              </p>
            </div>

            {/* Demonstrated value */}
            {timeFmt && (
              <div
                className="font-body text-center mt-6"
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1.5px solid #C8C8F4",
                  borderRadius: 16,
                  padding: "16px 18px",
                  fontSize: 14,
                  color: "#1C1A3E",
                }}
              >
                <span style={{ fontWeight: 700 }}>{timeFmt} saved this week</span> just on free.
                Imagine what Pro could hand back to you.
              </div>
            )}

            {/* Feature list, grouped by outcome */}
            <div className="mt-8 space-y-6">
              <FeatureGroup
                title="No more running out"
                items={[
                  "Unlimited messages — chat with Kin all day (free tier: 15/day)",
                  "Your last 50 conversations in the chat drawer — scroll back to what you already told Kin (free tier: 5)",
                  "Kin's own email address — forward school emails and receipts, and Kin catches every date (free tier: none)",
                ]}
              />
              <FeatureGroup
                title="Never re-explain your family"
                items={[
                  "Kin remembers every kid, allergy, and routine — no re-typing it into every chat",
                  "Family Profile stays the source of truth across meal plans, schedules, and lists",
                ]}
              />
              <FeatureGroup
                title="Hands-free help"
                items={[
                  "Voice conversations with Kin, not just typing",
                  "Higher daily proactiveness — Kin nudges you before you have to ask",
                  "Mom Brain Siri — ready-made Shortcuts for the chaos hours",
                ]}
              />
              <FeatureGroup
                title="Kin texts you"
                items={[
                  "Your morning, texted to you — like a best friend with a plan",
                  "Your news, your stocks, a movement nudge, a faith moment — you build the brief",
                ]}
              />
              <FeatureGroup
                title="Kin handles the recurring stuff"
                items={[
                  "Recurring tasks and reminders that just keep happening — restocks, refills, size checks",
                  "Morning briefings tuned to what your week actually looks like",
                ]}
              />
              <FeatureGroup
                title="Pro tools, first"
                items={[
                  "New Pro-only tools land here first — yours as they ship",
                ]}
              />
            </div>

            {/* Pricing */}
            <div className="mt-10 space-y-4">
              <PricingCard
                eyebrow="PAY MONTHLY"
                title="Kin Pro Monthly"
                price="$15/mo"
                subprice="billed monthly"
                fine="Cancel anytime."
                ctaLabel="Get Pro Monthly"
                loading={loadingPlan === "monthly"}
                disabled={loadingPlan !== null}
                onClick={() => handleUpgrade("monthly")}
              />
              <PricingCard
                eyebrow="BEST VALUE"
                title="Kin Pro Annual"
                price="$144/yr"
                subprice="$12/mo, billed annually"
                fine="Renews automatically."
                ctaLabel="Get Pro Annual"
                loading={loadingPlan === "annual"}
                disabled={loadingPlan !== null}
                onClick={() => handleUpgrade("annual")}
              />
              {/* #443 — founding pricing genuinely expires. After Aug 31 (ET)
                  the card is GONE, not relabelled: leaving it up would print a
                  passed deadline directly above a live $79 buy button. Monthly
                  and Annual are unaffected, and the space closes cleanly because
                  the parent uses space-y-4 rather than fixed heights. */}
              {isFoundingOfferOpen() && (
                <PricingCard
                  eyebrow="FOUNDING PRICING ENDS AUG 31"
                  title="Founding Member"
                  price="$79"
                  subprice="one-time, first year"
                  fine="One-time payment. 12 months of Pro, then falls back to free unless you resubscribe. No auto-renewal."
                  ctaLabel="Become a Founding Member"
                  loading={loadingPlan === "founding"}
                  disabled={loadingPlan !== null}
                  onClick={() => handleUpgrade("founding")}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const FeatureGroup = ({ title, items }: { title: string; items: string[] }) => (
  <div>
    <h3 className="font-display" style={{ fontSize: 18, color: "#1C1A3E" }}>
      {title}
    </h3>
    <ul className="mt-2 space-y-1.5">
      {items.map((item) => (
        <li key={item} className="font-body flex" style={{ fontSize: 14, color: "#54525C" }}>
          <span className="mr-2" style={{ color: "#5050C8" }}>•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const PricingCard = ({
  eyebrow,
  title,
  price,
  subprice,
  fine,
  ctaLabel,
  loading,
  disabled,
  onClick,
}: {
  eyebrow: string;
  title: string;
  price: string;
  subprice: string;
  fine: string;
  ctaLabel: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) => (
  <div
    style={{
      backgroundColor: "#FFFFFF",
      border: "1.5px solid #C8C8F4",
      borderRadius: 18,
      padding: 20,
    }}
  >
    <p
      className="font-body uppercase font-semibold"
      style={{ fontSize: "11px", letterSpacing: "0.5px", color: "#5050C8" }}
    >
      {eyebrow}
    </p>
    <h3 className="font-display mt-1" style={{ fontSize: 20, color: "#1C1A3E" }}>
      {title}
    </h3>
    <p className="font-body mt-1" style={{ fontSize: 22, fontWeight: 700, color: "#1C1A3E" }}>
      {price}{" "}
      <span className="font-body" style={{ fontSize: 13, fontWeight: 500, color: "#54525C" }}>
        {subprice}
      </span>
    </p>
    <button
      onClick={onClick}
      disabled={disabled}
      className="font-body w-full mt-4"
      style={{
        backgroundColor: "#5050C8",
        color: "#FFFFFF",
        fontSize: 15,
        fontWeight: 600,
        padding: "13px 0",
        borderRadius: 999,
        border: "none",
        opacity: disabled && !loading ? 0.6 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {loading ? "Opening checkout…" : ctaLabel}
    </button>
    <p className="font-body mt-3" style={{ fontSize: 11.5, color: "#8A889C" }}>
      {fine}
    </p>
  </div>
);

export default Pro;
